import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Button,
  IconButton,
  Typography,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  TextField,
  Tooltip,
} from '@material-ui/core';
import {
  Search as SearchIcon,
  Visibility,
  ChevronLeft,
  ChevronRight,
  BatteryStd,
  Close,
} from '@material-ui/icons';

import PropTypes from 'prop-types';
import qs from 'querystring';

import { Modal } from '~/components';
import { MultiAutocomplete } from '~/components/Form';
import { t, number, can } from '~/helpers';
import api from '~/services/api';

import * as S from '../../styles';
import DetalhesConfig from '../DetalhesConfig';
import TankWithSensor from '../TankWithSensor';

import {
  MODULO_FERROVIA
} from '~/constants/modules';

function somarColuna(array, coluna) {
  return array.reduce((acc, item) => {
    const valor = parseFloat(item[coluna]);
    if (!isNaN(valor)) {
      return acc + valor;
    }
    return acc;
  }, 0);
}

export default function Card({ params }) {
  const { innerWidth } = window;
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(0);
  const [perPage, setPerPage] = useState(12);
  const [inputPerPage, setInputPerPage] = useState(12);
  const [page, setPage] = useState(1);
  const [listapostos, setListapostos] = useState([]);
  const [postos, setPostos] = useState('');
  const [produtos, setProdutos] = useState('');
  const [timeOut, setTimeOut] = useState(0);
  const [search, setSearch] = useState({});
  const [somaCalcCapacidade, setSomaCalcCapacidade] = useState(0);
  const [somaEstoqueUtil, setSomaEstoqueUtil] = useState(0);
  const [somaCalcConsumoMedioDia, setSomaCalcConsumoMedioDia] = useState(0);
  const [somaNumeroCarretas, setSomaNumeroCarretas] = useState(0);
  const [somaEsperado, setSomaEsperado] = useState(0);

  const currentPage = useMemo(
    () => (Number(page - 1) === 0 ? pageSize : Number(page - 1)),
    [page, pageSize]
  );

  const handleChangeInputPerPage = useCallback(({ target }) => {
    setInputPerPage(target.value);
  }, []);

  const handleApplyPerPage = useCallback(() => {
    const value = Number(inputPerPage);

    if (!value || value < 1) {
      setInputPerPage(perPage);
      return;
    }

    const normalizedValue = Math.min(value, 50);

    setInputPerPage(normalizedValue);
    setPerPage(normalizedValue);
    setTimeOut(0);
    setPage(1);
  }, [inputPerPage, perPage]);

  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('pt-BR');
  const formattedTime = currentDate.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const handleGetResponseAPI = useCallback(async () => {
    try {
      setLoading(true);

      const wherefield = [];
      const valuewherefield = [];
      const typewherefield = [];

      if (postos.length > 0) {
        const listPostos = postos.map((p) => p.idposto);
        wherefield.push('idposto');
        valuewherefield.push(listPostos.join(','));
        typewherefield.push('IN');
      }

      if (produtos.length > 0) {
        wherefield.push('idproduto');
        const listProdutos = produtos.map((p) => p.idproduto);
        valuewherefield.push(listProdutos.join(','));
        typewherefield.push('IN');
      }

      const searchLocal = { wherefield, valuewherefield, typewherefield };

      const { data } = await api.get('/estoquepostomonitor', {
        params: {
          ...params,
          ...search,
          ...searchLocal,
          page,
          pagesize: perPage,
        },
        paramsSerializer: (param) => qs.stringify(param),
      });

      if (data) {
        setPage(data.page < data.totalPages ? Number(data.page + 1) : 1);
        setPageSize(data.totalPages);
        setListapostos(data.items);

        const somacalc_capacidade = somarColuna(data.items, 'calc_capacidade');
        setSomaCalcCapacidade(somacalc_capacidade);

        const somaestoque_util = somarColuna(data.items, 'calc_estoqueutil');
        setSomaEstoqueUtil(somaestoque_util);

        const somacalc_consumo_medio_dia = somarColuna(data.items, 'calc_consumo_medio_dia');
        setSomaCalcConsumoMedioDia(somacalc_consumo_medio_dia);

        const somacalc_numero_carretas = somarColuna(data.items, 'calc_numero_carretas_roundtrunk');
        setSomaNumeroCarretas(somacalc_numero_carretas);

        const somaesperadololca = somarColuna(data.items, 'calc_esperado');
        setSomaEsperado(somaesperadololca);

      } else {
        throw new Error('Resposta da API inválida');
      }
    } catch (error) {
      toast.error(error.message || 'Erro ao buscar os dados.');
    } finally {
      setLoading(false);
    }
  }, [params, page, perPage, search, postos, produtos]);

  const handleNextOrPreviewPage = useCallback((number) => {
    setPageSize(0);
    setTimeOut(0);
    setPage(number);
  }, []);

  useEffect(() => {
    setPageSize(0);
    setTimeOut(0);
    setPage(1);
  }, [params]);

  useEffect(() => {
    handleGetResponseAPI();
  }, [postos, produtos]);

  useEffect(() => {
    const isTime = setInterval(() => {
      handleGetResponseAPI();
      setTimeOut(150000); // setTimeOut(10000); // setTimeOut(150000);
    }, timeOut);

    return () => clearInterval(isTime);
  }, [handleGetResponseAPI, timeOut]);

  const modalRef = useRef(null);
  const modalActions = useMemo(
    () => [
      {
        label: t('s.geral.botaocancelar'),
        callback: () => modalRef.current.handleCloseModal(),
        color: 'secondary',
        variant: 'outlined',
        startIcon: <Close />,
      },
    ],
    []
  );

  function renderTelemetriaCell(statusTanque, backgroundColor) {
    switch (statusTanque) {
      case 0:
        return (
          <Tooltip title={t('s.estoquepostomonitor.tanquessemsonda')}>
            <BatteryStd
              style={{
                fontSize: 24,
                color: 'gray',
                transform: 'scaleX(1.5)',
              }}
            />
          </Tooltip>
        );

      case 1:
        return (
          <TankWithSensor
            colorSensor="green"
            colorTank="green"
            background={backgroundColor}
            tooltip={t('s.estoquepostomonitor.tanquescomsondaonline')}
          />
        );

      case 2:
        return (
          <TankWithSensor
            colorSensor="red"
            colorTank="red"
            background={backgroundColor}
            tooltip={t('s.estoquepostomonitor.tanquescomsondaoffline')}
          />
        );

      case 3:
        return (
          <TankWithSensor
            colorSensor="red"
            colorTank="gray"
            background={backgroundColor}
            tooltip={t('s.estoquepostomonitor.tanquessemecomsondaoffline')}
          />
        );

      case 4:
        return (
          <TankWithSensor
            colorSensor="green"
            colorTank="gray"
            background={backgroundColor}
            tooltip={t('s.estoquepostomonitor.tanquessemecomsondaonline')}
          />
        );

      default:
        return <></>;
    }
  }

  return (
    <>
      {loading && <LinearProgress />}

      <S.WrapperContainer component={Paper}>
        <div>
          <p>
            {t('s.relatorio.afericao.data')}: {formattedDate} {formattedTime}
          </p>
        </div>
        <div style={{ height: '9rem' }}>
          <S.Container>
            <S.Pagination>
              <div className="inputs-container">
                <MultiAutocomplete
                  style={{
                    width: '380px',
                    minWidth: '200px',
                    flex: '1 1 auto',
                  }}
                  multiple
                  name="idposto"
                  label="codigonomeposto"
                  url="postos"
                  unique="idposto"
                  title={t('s.relatorio.alteracaoencerrante.posto')}
                  hiddens={[{ name: 'codigonomeposto' }]}
                  getValue={(value) => setPostos(value)}
                />
                <MultiAutocomplete
                  style={{
                    width: '380px',
                    minWidth: '100px',
                    flex: '1 1 auto',
                  }}
                  multiple
                  name="idproduto"
                  label="nomeproduto"
                  url="produtos"
                  unique="idproduto"
                  title={t('s.menu.configuracao.produto')}
                  hiddens={[{ name: 'nomeproduto' }]}
                  getValue={(value) => setProdutos(value)}
                />
              </div>
              <div className="actions-container">
                <Typography variant="body2">
                  {t('s.estoquepostomonitor.registrosporpagina')}:
                </Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  type="number"
                  className="inputPerPage"
                  value={inputPerPage}
                  disabled={loading}
                  onChange={handleChangeInputPerPage}
                  onBlur={handleApplyPerPage}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleApplyPerPage();
                    }
                  }}
                  inputProps={{
                    min: 1,
                    max: 50,
                  }}
                />
                <Typography variant="body2">
                  {currentPage} de {pageSize}
                </Typography>
                <IconButton
                  size="small"
                  color="default"
                  disabled={currentPage === 1 || loading}
                  onClick={() =>
                    handleNextOrPreviewPage(Number(currentPage - 1))
                  }
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  size="small"
                  color="default"
                  disabled={currentPage === pageSize || loading}
                  onClick={() => handleNextOrPreviewPage(page)}
                >
                  <ChevronRight />
                </IconButton>
              </div>
            </S.Pagination>
          </S.Container>
        </div>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              {((can(MODULO_FERROVIA)) && (
                <TableCell align="left" style={{ width: '10rem' }}>
                  {t('s.controleestoque.corredor')}
                </TableCell>
              ))}

              <TableCell align="left" style={{ width: '15rem' }}>
                {t('s.configuracao.terminal.nomeposto')}
              </TableCell>

              <TableCell align="right" style={{ width: '10rem' }}>
                {t('s.controleestoque.espacovazio')}
              </TableCell>
              {/* <TableCell align="right" style={{ width: '17rem' }}>
                {t('s.configuracao.tanquefisico.volumeperacionaltanquefisico')}
              </TableCell> */}
              <TableCell align="right" style={{ width: '17rem' }}>
                {t('s.estoquepostomonitor.esperado')}
              </TableCell>
              <TableCell align="right" style={{ width: '17rem' }}>
                {t('s.estoquepostomonitor.numerocarretas')}
              </TableCell>

              <TableCell align="right" style={{ width: '17rem' }}>
                {t('s.estoquepostomonitor.esotqueutil')}
              </TableCell>
              <TableCell align="right" style={{ width: '17rem' }}>
                {t('s.estoquepostomonitor.consumomedio')}
              </TableCell>
              <TableCell align="right" style={{ width: '17rem' }}>
                {t('s.estoquepostomonitor.duracaoestoque')}
              </TableCell>

              <TableCell align="right" style={{ width: '17rem' }}>
                {t('s.cadastro.veiculo.volumetanqueveiculo')} (l)
              </TableCell>

              <TableCell align="center" style={{ width: '10rem' }}>
                {t('s.configuracao.terminal.telemetriaterminal')}
              </TableCell>
              <TableCell align="left" style={{ width: '20rem' }}>
                {t('s.relatorio.desempenhoveiculo.produto')}
              </TableCell>

              <TableCell align="center">
                {t('s.geral.visualizar')}
              </TableCell>

            </TableRow>
          </TableHead>
          <TableBody>
            {listapostos.map((item, index) => (
              <TableRow key={`${item.idcliente}-${item.idposto}-${item.idproduto}`}>

                {((can(MODULO_FERROVIA)) && (
                  <TableCell align="left" style={{ whiteSpace: 'nowrap' }}>
                    {item.corredor}
                  </TableCell>
                ))}

                <TableCell align="left" style={{ whiteSpace: 'nowrap' }}>
                  {item.nomeposto}
                </TableCell>

                <TableCell align="right" style={{ color: 'black', backgroundColor: `#FDE3D6` }}>
                  {number(item.calc_espaco_vazio, 0)}
                </TableCell>

                {/* <TableCell align="right">
                  <span>{`${number(item.calc_volumeporcento80, 0)}`}</span>
                  <span
                    style={{ fontSize: 9, fontStyle: 'italic' }}
                  >{` (${item.fatorestoqueporcentopostoprodutoconfig}%)`}</span>
                </TableCell> */}

                <TableCell align="right" style={{ color: 'black', backgroundColor: `#FDE3D6` }}>
                  {number(item.calc_esperado, 0)}
                </TableCell>


                <TableCell align="right" style={{ color: 'black', backgroundColor: `#FDE3D6` }}>
                  {number(item.calc_numero_carretas_roundtrunk, 0)}
                </TableCell>

                <TableCell align="right">
                  {number(item.calc_estoqueutil)}
                </TableCell>
                <TableCell align="right" style={{ color: 'black' }}>
                  {number(item.calc_consumo_medio_dia, 0)}
                </TableCell>

                <TableCell
                  align="right"
                  style={{
                    color: '#FFFFFF',
                    backgroundColor: `${item.calc_duracao_estoque_dias_status}`,
                  }}
                >
                  {number(item.calc_duracao_estoque_dias, 1)}
                </TableCell>



                <TableCell align="right">
                  {number(item.calc_capacidade)}
                </TableCell>

                <TableCell align="center" style={{ padding: 0 }}>
                  {renderTelemetriaCell(
                    item.tipostatustanquefisico,
                    index % 2 === 1 ? '#fbfbfb' : '#ffffff'
                  )}
                </TableCell>
                <TableCell align="left">{String(item.nomeproduto)}</TableCell>
                <TableCell align="center">
                  <Tooltip title={t('s.geral.visualizar')}>
                    <IconButton
                      size="small"
                      aria-label={t('s.geral.visualizar')}
                      onClick={() =>
                        modalRef.current.handleOpenModal({ data: item })
                      }
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>

          <TableFooter>
            <TableRow>
              <S.CustomTableCellFooter >Total</S.CustomTableCellFooter >
              <TableCell></TableCell>
              {((can(MODULO_FERROVIA)) && (
                <TableCell align="right"></TableCell>
              ))}

              <S.CustomTableCellFooter align="right" >{number(somaEsperado, 0)} </S.CustomTableCellFooter>
              <S.CustomTableCellFooter align="right" >{number(somaNumeroCarretas, 0)}</S.CustomTableCellFooter>
              <TableCell align="right"></TableCell>
              <S.CustomTableCellFooter align="right" >{number(somaCalcConsumoMedioDia, 0)}</S.CustomTableCellFooter>
              {/* <S.CustomTableCellFooter align="right" >{number(somaVolumeDeSeguraca, 0)}</S.CustomTableCellFooter> */}
              <TableCell align="right"></TableCell>
              <S.CustomTableCellFooter align="right" >{number(somaCalcCapacidade, 0)} </S.CustomTableCellFooter>
              <TableCell align="right"></TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableFooter>

        </Table>
      </S.WrapperContainer>
      <Modal
        ref={modalRef}
        title="Detalhe da config"
        component={DetalhesConfig}
        actions={modalActions}
        maxWidth="sm"
        maxHeight="60vh"
      />
    </>
  );
}

Card.propTypes = {
  params: PropTypes.objectOf(PropTypes.any),
};

Card.defaultProps = {
  params: {},
};
